import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { conns } from '@cfg/axiosConfig.tsx';
import type { PosSaleLine } from '../types';

export interface CommissionPreviewEntry {
  item_id: number;
  amount: number;
  agent_id?: number | null;
  agreement_id?: number | null;
  metadata?: Record<string, unknown>;
}

const fetchPreview = async (lines: PosSaleLine[]): Promise<CommissionPreviewEntry[]> => {
  const params = {
    item_id: lines.map((line) => line.item.id),
    qty: lines.map((line) => line.quantity),
    unit_price: lines.map((line) => Number(line.item.price?.value ?? 0)),
    professional_id: lines.map((line) => line.professionalId ?? ''),
  };
  const { data } = await conns<{ results: CommissionPreviewEntry[] }>('erp:pos:commissions:preview').get(params);
  return data.results ?? [];
};

export const useCommissionPreview = (lines: PosSaleLine[], enabled: boolean) => {
  const eligibleLines = useMemo(
    () => lines.filter((line) => line.quantity > 0),
    [lines],
  );
  const key = eligibleLines
    .map((line) => `${line.item.id}:${line.quantity}:${line.item.price?.value ?? 0}:${line.professionalId ?? 'none'}`)
    .join('|');

  return useQuery({
    queryKey: ['pos', 'commissions', 'preview', key],
    queryFn: () => fetchPreview(eligibleLines),
    enabled: enabled && eligibleLines.length > 0,
    staleTime: 30_000,
    retry: 0,
  });
};

export default useCommissionPreview;
